"use client";

import type { ReactNode } from "react";
import * as s from "@/lib/styles";
import {
  accountBroker,
  accountNickname,
  brokerDisplayName,
  brokerLogoSrc,
  type AccountNameFields,
} from "@/lib/account-name";

export type StrategyHeaderAccount = AccountNameFields;

export function BrokerLogo({
  account,
  className = "h-20 w-20 object-contain rounded shrink-0",
}: {
  account: StrategyHeaderAccount;
  className?: string;
}) {
  const broker = accountBroker(account);
  const src = brokerLogoSrc(broker);
  if (!src) return null;
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={src} alt={brokerDisplayName(broker)} className={className} />
  );
}

/** Strategy page title stack: broker logo | name / nickname / Config. */
export function StrategyPageHeader({
  title,
  account,
  configButton,
  trailing,
  logoSlotId,
  detailsSlotId,
  analysisSlotId,
  analysis,
}: {
  title: string;
  account?: StrategyHeaderAccount | null;
  configButton?: ReactNode;
  trailing?: ReactNode;
  logoSlotId?: string;
  detailsSlotId?: string;
  analysisSlotId?: string;
  /** Rendered in the analysis slot directly, for pages that own their header. */
  analysis?: ReactNode;
}) {
  const nickname = account ? accountNickname(account) : "";

  const logo = logoSlotId ? (
    <div id={logoSlotId} className="shrink-0 empty:hidden" />
  ) : (
    account && <BrokerLogo account={account} />
  );
  const labels = (
    <div className="flex flex-col items-start gap-1.5 min-w-0">
      <h1 className={s.pageTitle + " !mb-0 !leading-none"}>{title}</h1>
      {detailsSlotId ? (
        <div id={detailsSlotId} className="flex flex-col items-start gap-1.5 empty:hidden" />
      ) : (
        <>
          {nickname ? (
            <span className="text-sm text-amber font-mono whitespace-nowrap">{nickname}</span>
          ) : null}
          {configButton}
        </>
      )}
    </div>
  );

  return analysisSlotId || analysis ? (
    <div className="grid grid-cols-[3fr_7fr] gap-3 items-start mt-[15px] mb-[10px]">
      <div className="flex items-start gap-3 min-w-0">
        {logo}
        {labels}
      </div>
      <div id={analysisSlotId} className="min-w-0 empty:hidden self-start">{analysis}</div>
    </div>
  ) : (
    <div className="mb-6">
      <div className="flex items-start gap-3 mb-4">
        {logo}
        {labels}
        {trailing && (
          <div className="flex items-center gap-2 ml-auto self-center">{trailing}</div>
        )}
      </div>
    </div>
  );
}
